import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  EnvelopeIcon, MapPinIcon, PaperAirplaneIcon, CheckCircleIcon, PhoneIcon,
  CodeBracketIcon, MagnifyingGlassIcon, PuzzlePieceIcon, EllipsisHorizontalCircleIcon,
  GlobeAltIcon, ServerStackIcon, ArrowRightIcon
} from '@heroicons/react/24/outline';
import Turnstile from 'react-turnstile'; 
import SEO from '../components/SEO';
import PageHeader from '../components/PageHeader';
import SchemaMarkup from '../components/SchemaMarkup';

// --- STATIC DATA (Defined outside component to prevent re-renders) ---
const projectTypes = [
  { id: 'web', label: 'Web App' },
  { id: 'mobile', label: 'Mobile App' },
  { id: 'ecommerce', label: 'E-Commerce' },
  { id: 'iot', label: 'IoT / Embedded' },     
  { id: 'cloud', label: 'Cloud / DevOps' },   
  { id: 'custom', label: 'Custom Software' }  
];

// --- SUB-COMPONENT (Defined outside main component) ---
const TopicCard = ({ value, label, desc, current, onClick, icon }) => {
  const Icon = icon;
  return (
    <button
      type="button"
      onClick={() => onClick(value)}
      aria-pressed={current === value}
      className={`text-left p-4 rounded-xl border-2 transition-all duration-300 group relative overflow-hidden h-full
        ${current === value
          ? 'border-slate-900 bg-slate-50'
          : 'border-slate-100 bg-white hover:border-blue-400 hover:shadow-lg'
        }`}
    >
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 transition-colors ${current === value ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-500 group-hover:bg-blue-50 group-hover:text-blue-600'}`}>
            <Icon className="w-6 h-6" />
        </div>
        <div className="font-bold text-slate-900 text-sm mb-1">{label}</div>
        <div className="text-xs text-slate-500 leading-relaxed">{desc}</div>

        {current === value && (
            <div className="absolute top-4 right-4 w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
        )}
    </button>
  );
};

export default function Contact() {
  const { t, i18n } = useTranslation();
  const langPrefix = i18n.language === 'et' ? '/et' : '/en';
  
  const [status, setStatus] = useState(null);
  const [captchaError, setCaptchaError] = useState(null);
  const [turnstileToken, setTurnstileToken] = useState(null);
  
  // Logic State
  const [topic, setTopic] = useState('development'); 
  const [devType, setDevType] = useState('web'); 
  
  const SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY; 

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!turnstileToken) {
      setCaptchaError(t('contact_page.error.captcha') || 'Please verify you are human first.');
      return;
    }
    setCaptchaError(null);
    setStatus('loading');
    const formData = new FormData(e.target);
    
    const payload = {
      name: formData.get('user_name'),
      email: formData.get('user_email'),
      phone: formData.get('user_phone'),
      message: formData.get('message'),
      topic,
      meta_data: {
          dev_type: topic === 'development' ? devType : null,
          target_url: topic === 'audit' ? formData.get('target_url') : null,
          systems: topic === 'integration' ? formData.get('systems') : null
      },
      token: turnstileToken
    };

    try {
      const response = await fetch('/api/send-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (response.ok) setStatus('success');
      else setStatus('error');
    } catch (error) {
      console.error(error);
      setStatus('error');
    }
  };

  return (
    <div className="bg-slate-50 min-h-screen pb-20">
      <SEO title={t('contact_page.title')} description={t('contact_page.meta_desc')} />
      <SchemaMarkup type="contact-page" data={{ title: t('contact_page.title'), description: t('contact_page.meta_desc'), url: `${langPrefix}/contact` }} />

      <PageHeader title={t('contact_page.heading')} subtitle={t('contact_page.subheading')} maxWidth="max-w-6xl" />
      
      <div className="container mx-auto px-4 py-12 md:py-20 max-w-6xl">
          <div className="grid lg:grid-cols-12 gap-8 items-start">
            
            {/* --- LEFT: INFO --- */}
            <div className="lg:col-span-4 bg-slate-900 text-white p-8 md:p-10 rounded-2xl shadow-xl shadow-slate-900/20 flex flex-col justify-between border border-slate-800 min-h-[450px]">
              <div>
                <h2 className="text-xl font-bold mb-8 text-white uppercase tracking-wider">{t('contact_page.info.title')}</h2>
                <div className="space-y-8">
                  <div className="flex items-start gap-4">
                    <div className="mt-1 p-2 bg-slate-800 rounded-lg border border-slate-700"><EnvelopeIcon className="h-5 w-5 text-blue-400" /></div>
                    <div><p className="text-xs text-slate-400 uppercase tracking-widest font-bold mb-1">{t('contact_page.info.email_label')}</p><p className="text-lg font-medium text-white">info@mupax.eu</p></div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="mt-1 p-2 bg-slate-800 rounded-lg border border-slate-700"><MapPinIcon className="h-5 w-5 text-blue-400" /></div>
                    <div><p className="text-xs text-slate-400 uppercase tracking-widest font-bold mb-1">{t('contact_page.info.location')}</p><p className="text-lg font-medium text-white">Tallinn, Estonia</p></div>
                  </div>
                </div>
              </div>
              <div className="mt-12 pt-8 border-t border-slate-800"><p className="text-slate-300 text-base italic">"{t('contact_page.info.quote')}"</p></div>
            </div>

            {/* --- RIGHT: FORM --- */}
            <div className="lg:col-span-8 bg-white p-8 md:p-10 rounded-2xl shadow-sm border border-slate-200">
              {status === 'success' ? (
                <div className="text-center py-12">
                    <CheckCircleIcon className="w-16 h-16 text-green-500 mx-auto mb-4"/>
                    <h3 className="text-2xl font-bold text-slate-900">{t('contact_page.success.title')}</h3>
                    <p className="text-slate-600 mt-2 mb-6">{t('contact_page.success.desc')}</p>
                    <button onClick={() => setStatus(null)} className="text-slate-900 font-bold underline">{t('contact_page.success.again')}</button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-8">
                  {status === 'error' && (
                    <div role="alert" className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm font-medium">
                      {t('contact_page.error.title') || 'Something went wrong. Please try again.'}
                    </div>
                  )}
                  
                  {/* SECTION 1: TOPIC SELECTION */}
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-4 ml-1">{t('contact_page.form.topic_label')}</label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <TopicCard 
                            value="development" 
                            current={topic} 
                            onClick={setTopic}
                            label={t('contact_page.topics.development')} 
                            desc={t('contact_page.topics.dev_desc')} 
                            icon={CodeBracketIcon} 
                        />
                        <TopicCard 
                            value="audit" 
                            current={topic} 
                            onClick={setTopic}
                            label={t('contact_page.topics.audit')} 
                            desc={t('contact_page.topics.audit_desc')} 
                            icon={MagnifyingGlassIcon} 
                        />
                        <TopicCard 
                            value="integration" 
                            current={topic} 
                            onClick={setTopic}
                            label={t('contact_page.topics.integration')} 
                            desc={t('contact_page.topics.int_desc')} 
                            icon={PuzzlePieceIcon} 
                        />
                        <TopicCard 
                            value="other" 
                            current={topic} 
                            onClick={setTopic}
                            label={t('contact_page.topics.other')} 
                            desc={t('contact_page.topics.other_desc')} 
                            icon={EllipsisHorizontalCircleIcon} 
                        />
                    </div>
                  </div>

                  {/* SECTION 2: DYNAMIC FIELDS */}
                  <div className="bg-slate-50 p-6 rounded-xl border border-slate-100 transition-all duration-500 ease-in-out">
                    
                    {/* SCENARIO A: DEVELOPMENT */}
                    {topic === 'development' && (
                        <div className="animate-fade-in-down">
                            <label id="stack-label" className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">{t('contact_page.form.stack_label')}</label>
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" role="group" aria-labelledby="stack-label">
                                {projectTypes.map((type) => (
                                    <button
                                        key={type.id}
                                        type="button"
                                        onClick={() => setDevType(type.id)}
                                        aria-pressed={devType === type.id}
                                        className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${devType === type.id ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'}`}
                                    >
                                        {t(`contact_page.subtypes.${type.id}`) || type.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* SCENARIO B: AUDIT */}
                    {topic === 'audit' && (
                        <div className="animate-fade-in-down">
                            <label htmlFor="contact-url" className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">{t('contact_page.form.url_label')}</label>
                            <div className="flex items-center bg-white border border-slate-200 rounded-lg px-3 focus-within:border-blue-600 focus-within:ring-1 focus-within:ring-blue-600">
                                <GlobeAltIcon className="w-5 h-5 text-slate-400 mr-2" />
                                <input id="contact-url" type="url" name="target_url" className="w-full py-3 outline-none text-sm text-slate-900" placeholder="https://example.com" />
                            </div>
                        </div>
                    )}

                    {/* SCENARIO C: INTEGRATION */}
                    {topic === 'integration' && (
                        <div className="animate-fade-in-down">
                            <label htmlFor="contact-systems" className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">{t('contact_page.form.systems_label')}</label>
                            <div className="flex items-center bg-white border border-slate-200 rounded-lg px-3 focus-within:border-blue-600 focus-within:ring-1 focus-within:ring-blue-600">
                                <ServerStackIcon className="w-5 h-5 text-slate-400 mr-2" />
                                <input id="contact-systems" type="text" name="systems" className="w-full py-3 outline-none text-sm text-slate-900" placeholder="e.g. Magento + Erply..." />
                            </div>
                        </div>
                    )}
                    
                    {/* SCENARIO D: OTHER */}
                    {topic === 'other' && (
                        <p className="text-sm text-slate-500 italic animate-fade-in-down">
                            {t('contact_page.form.other_hint')}
                        </p>
                    )}
                  </div>

                  {/* SECTION 3: BASIC INFO */}
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="contact-name" className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2 ml-1">{t('contact_page.form.name')}</label>
                      <input id="contact-name" type="text" name="user_name" required className="w-full px-4 py-3 bg-white rounded-lg border border-slate-200 focus:border-blue-600 focus:ring-1 focus:ring-blue-600 outline-none text-slate-900" placeholder={t('contact_page.form.name_placeholder')} />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2 ml-1">{t('contact_page.form.email')}</label>
                      <input id="contact-email" type="email" name="user_email" required className="w-full px-4 py-3 bg-white rounded-lg border border-slate-200 focus:border-blue-600 focus:ring-1 focus:ring-blue-600 outline-none text-slate-900" placeholder={t('contact_page.form.email_placeholder')} />
                    </div>
                    {/* PHONE FIELD */}
                    <div>
                        <label htmlFor="contact-phone" className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2 ml-1">{t('contact_page.form.phone')}</label>
                        <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <PhoneIcon className="h-5 w-5 text-slate-400" />
                            </div>
                            <input id="contact-phone" type="tel" name="user_phone" className="w-full pl-10 px-4 py-3 bg-white rounded-lg border border-slate-200 focus:border-blue-600 focus:ring-1 focus:ring-blue-600 outline-none text-slate-900" placeholder="+372 5555 1234" />
                        </div>
                    </div>
                  </div>

                  {/* MESSAGE & SUBMIT */}
                  <div>
                    <label htmlFor="contact-message" className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2 ml-1">{t('contact_page.form.message')}</label>
                    <textarea id="contact-message" name="message" rows="4" required className="w-full px-4 py-3 bg-white rounded-lg border border-slate-200 focus:border-blue-600 focus:ring-1 focus:ring-blue-600 outline-none resize-none text-slate-900" placeholder={t('contact_page.placeholders.default')}></textarea>
                  </div>
                  
                  <div className="pt-4 flex flex-col md:flex-row items-center justify-between gap-6">
                    <div className="flex flex-col gap-2">
                      <Turnstile
                        sitekey={SITE_KEY}
                        onVerify={(token) => { setTurnstileToken(token); setCaptchaError(null); }}
                        theme="light"
                      />
                      {captchaError && (
                        <p role="alert" className="text-red-600 text-sm font-medium">{captchaError}</p>
                      )}
                    </div>

                    <button
                      type="submit"
                      disabled={status === 'loading'}
                      className={`
                        w-full sm:w-auto py-4 px-10 rounded-full font-bold shadow-lg transition-all flex items-center justify-center gap-2
                        ${status === 'loading'
                          ? 'bg-slate-300 cursor-not-allowed text-slate-500'
                          : 'bg-blue-600 hover:bg-blue-500 text-white hover:shadow-xl hover:scale-105'
                        }
                      `}
                    >
                        {status === 'loading' ? (
                          <span>{t('contact_page.form.sending')}</span>
                        ) : (
                          <>
                            {t('contact_page.form.send')} 
                            {/* Using the same Arrow icon as your link for consistency */}
                            <ArrowRightIcon className="w-4 h-4" />
                          </>
                        )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
      </div>
    </div>
  );
}
